import { useState, useEffect, useRef, useCallback } from 'react'
import keycloak from '@/lib/keycloak'
import { extractOrgId } from '@/lib/jwtAuth'

interface KeycloakState {
  ready:     boolean   // init completed (success or error)
  token:     string | null
  orgId:     string | null
  error:     string | null
}

interface KeycloakAuth extends KeycloakState {
  // Forces Keycloak to mint a fresh token (e.g. right after org creation, so a
  // newly-granted org claim is picked up immediately instead of on the next
  // natural refresh cycle).
  refresh: () => Promise<void>
}

const REFRESH_INTERVAL_MS = 60_000  // check every 60s
const REFRESH_IF_EXPIRING  = 70     // refresh if token expires within 70s
const INIT_TIMEOUT_MS      = 15_000

function persist(token: string, orgId: string | null) {
  sessionStorage.setItem('token', token)
  // Must clear, not just skip setting — otherwise a stale orgId left over
  // from a previous account in this same tab leaks into API requests for a
  // genuinely org-less identity (e.g. a brand-new signup), since client.ts's
  // getAuthCredentials() falls back to this key.
  if (orgId) sessionStorage.setItem('organizationId', orgId)
  else sessionStorage.removeItem('organizationId')
}

export function useKeycloakAuth(): KeycloakAuth {
  const initialized = useRef(false)
  const intervalRef  = useRef<ReturnType<typeof setInterval> | null>(null)

  const [state, setState] = useState<KeycloakState>({
    ready: false, token: null, orgId: null, error: null,
  })

  useEffect(() => {
    // React StrictMode fires effects twice in dev — guard with a ref
    if (initialized.current) return
    initialized.current = true

    const timeout = setTimeout(() => {
      setState({ ready: true, token: null, orgId: null, error: 'Authentication service timed out. Please refresh.' })
    }, INIT_TIMEOUT_MS)

    keycloak
      .init({ onLoad: 'login-required', checkLoginIframe: false })
      .then(() => {
        clearTimeout(timeout)

        if (!keycloak.token) {
          // Keycloak didn't give a token — reload to retry login
          window.location.reload()
          return
        }

        const orgId = extractOrgId(keycloak.token)
        persist(keycloak.token, orgId)

        setState({ ready: true, token: keycloak.token, orgId, error: null })

        // Refresh token before expiry every 60s
        intervalRef.current = setInterval(() => {
          keycloak.updateToken(REFRESH_IF_EXPIRING)
            .then(refreshed => {
              if (refreshed && keycloak.token) {
                const newOrgId = extractOrgId(keycloak.token)
                persist(keycloak.token, newOrgId)
                setState(s => ({ ...s, token: keycloak.token!, orgId: newOrgId }))
              }
            })
            .catch(() => {
              // Refresh failed — session expired, force re-login
              clearInterval(intervalRef.current!)
              sessionStorage.clear()
              keycloak.login()
            })
        }, REFRESH_INTERVAL_MS)
      })
      .catch(err => {
        clearTimeout(timeout)
        console.error('[keycloak] init failed:', err)
        setState({ ready: true, token: null, orgId: null, error: 'Failed to connect to authentication service. Please refresh.' })
      })

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  const refresh = useCallback(async () => {
    await keycloak.updateToken(-1)
    if (keycloak.token) {
      const newOrgId = extractOrgId(keycloak.token)
      persist(keycloak.token, newOrgId)
      setState(s => ({ ...s, token: keycloak.token!, orgId: newOrgId }))
    }
  }, [])

  return { ...state, refresh }
}

// Call this anywhere to trigger Keycloak logout (clears session + redirects to KC logout page)
export async function keycloakLogout() {
  sessionStorage.removeItem('token')
  sessionStorage.removeItem('organizationId')
  const redirectUri = import.meta.env.VITE_BASE_APP_URL || window.location.origin
  await keycloak.logout({ redirectUri })
}
